import {sanitizeUrl} from '~/utils/sanitizers.js';

function buildSafeLink(url, text, attrs = '') {
  const safeUrl = sanitizeUrl(url);
  const safeText = text;

  if (!safeUrl) {
    return safeText;
  }

  return `<a href="${safeUrl}" ${attrs}>${safeText}</a>`;
}

export const DIGITIZATION_FRIENDLY_STATUSES = {
  ordered: {
    label: 'FOREVER Box Ordered',
    badgeIcon: 'i-ri-check-line',
    badgeColor: 'bg-heroblue',
    text: () => `You've taken the first step toward preserving your precious memories. Congratulations!`
  },
  originated: {
    label: 'Shipped to You',
    badgeIcon: 'i-ri-mail-send-fill',
    badgeColor: 'bg-heroblue',
    text: project => `We have shipped your FOREVER Digitization Box to you. Track its progress with your ${buildSafeLink(project.outgoing_tracking_link, 'tracking number', 'target="_blank" rel="noopener"')}. The next step is to pack up your memories!`
  },
  received: {
    label: 'Box Received at FOREVER',
    badgeIcon: 'i-ri-archive-line',
    badgeColor: 'bg-heroblue',
    text: () => 'Great job packing your Box and shipping it back to us! Your memories have arrived safely at our FOREVER Digitization Facility.'
  },
  inventoried: {
    label: 'Memories Inventoried',
    badgeIcon: 'i-ri-stack-line',
    badgeColor: 'bg-heroblue',
    text: project => `We've carefully tallied each memory you've sent us. Review your additional items and ${buildSafeLink(project.cart_link, 'check out to continue.', 'data-gtmid="core_ui-digi-overage_checkout"')}`
  },
  conversion_queued: {
    label: 'In Digitization Queue',
    badgeIcon: 'i-ri-bookmark-line',
    badgeColor: 'bg-heroblue',
    text: () => `Congratulations! Your digitization project is now in line. We'll let you know once we get started.`
  },
  in_conversion: {
    label: 'Being Digitized',
    badgeIcon: 'i-ri-projector-2-line',
    badgeColor: 'bg-heroblue',
    isInProgressWhenCurrent: true,
    text: () => `Using state-of-the-art technology and white-glove service, our team is carefully preserving your precious memories.`
  },
  quality_review: {
    label: 'Quality Review',
    badgeIcon: 'i-ri-shield-check-line',
    badgeColor: 'bg-heroblue',
    isInProgressWhenCurrent: true,
    text: () => `Our team is carefully inspecting each of your digitized memories to ensure that they're up to our high standards.`
  },
  conversion_complete: {
    label: 'Ready to View',
    badgeIcon: 'i-ri-checkbox-circle-line',
    badgeColor: 'bg-sage-800',
    text: () => `Great news - we're all done digitizing your memories! We're now carefully repackaging each item you entrusted to our care.`
  },
  ready_for_pickup: {
    label: 'Awaiting Pickup',
    badgeIcon: 'i-ri-building-4-line',
    badgeColor: 'bg-sage-800',
    isInProgressWhenCurrent: true,
    text: () => `Your original items are waiting to be picked up at our <a href="https://goo.gl/maps/aDUZ2vaeviRGiHyM8" target="_blank" rel="noopener">FOREVER Digitization Facility</a>.`
  },
  picked_up: {
    label: 'Picked Up',
    badgeIcon: 'i-ri-checkbox-circle-line',
    badgeColor: 'bg-sage-800',
    text: () => 'Enjoy your precious memories!'
  },
  shipped: {
    label: 'Shipped Back to You',
    badgeIcon: 'i-ri-mail-send-fill',
    badgeColor: 'bg-sage-800',
    text: project => `Your FOREVER Digitization Box has been shipped back to you! Track its progress with your ${buildSafeLink(project.tracking_link, 'tracking number.', 'target="_blank" rel="noopener"')}`
  },

  cancelled: {
    label: 'Cancelled',
    badgeIcon: 'i-ri-delete-back-line',
    badgeColor: 'bg-gray-400',
    text: () => 'This digitization project was cancelled.'
  },
  gifted: {
    label: 'Gifted',
    badgeIcon: 'i-ri-gift-line',
    badgeColor: 'bg-gray-400',
    text: () => `The person who received your gifted Box has sent it in. They can now track its progress from their FOREVER Account.`
  },
  transferred: {
    label: 'Transferred',
    badgeIcon: 'i-ri-exchange-line',
    badgeColor: 'bg-gray-400',
    text: () => `The person who received your transferred Box has sent it in. They can now track its progress from their FOREVER Account.`
  }
};

export function mapDigitizationFile({file, config, user}) {
  const typeConfig = config || Object.values(DIGITIZATION_FILE_TYPES).find(t => t.type === file.file_type.toLowerCase());

  const theUser = (()=>{
    if (user) {
      return {
        ...user,
        profileClassName: getProfileClassNameFromUUID(user.id),
      };
    } else {
      return {
        ...file.customer,
        profileClassName: getProfileClassNameFromUUID(file.customer.id),
        avatarImage: file.customer?.avatar_url
      };
    }
  })()

  return {
    ...file,
    user: theUser,
    fileType: typeConfig.type,
    type: typeConfig.type,
    name: file.file_name,
    is: {
      detail: true,
      video: typeConfig.type === 'video',
      audio: typeConfig.type === 'audio',
      photo: typeConfig.type === 'photo',
      streamable: typeConfig.isStreamable || false
    }
  };
}

export function mapDigitizationProject(project, {mapFiles} = {}) {
  const mappedProject = Object.assign({}, project);
  const dayjs = useDayjs();

  mappedProject.name = `Box #${mappedProject.id}`;
  mappedProject.orderDateText = mappedProject.ordered_at ? `Ordered ${dayjs(mappedProject.ordered_at).format('l')}` : null;

  mappedProject.hasStoragePermanent = mappedProject.storage_transfer_status === DIGITIZATION_STORAGE_TRANSFER_STATUSES.complete;
  mappedProject.hasStorageInProgress = mappedProject.storage_transfer_status === DIGITIZATION_STORAGE_TRANSFER_STATUSES.inProgress;
  mappedProject.hasStorageNotStarted = mappedProject.storage_transfer_status === DIGITIZATION_STORAGE_TRANSFER_STATUSES.notStarted;

  mappedProject.isSharable = mappedProject.complete && !mappedProject.download_expired;

  mappedProject.user = {
    ...mappedProject.customer,
    profileClassName: mappedProject.customer?.id ? getProfileClassNameFromUUID(mappedProject.customer?.id) : null,
    avatarImage: mappedProject.customer?.avatar_url
  }

  if (mappedProject.progress_bar_status) {
    mappedProject.currentStep = {
      progress_bar_status: mappedProject.progress_bar_status,
      ...DIGITIZATION_FRIENDLY_STATUSES[mappedProject.progress_bar_status],
      text: DIGITIZATION_FRIENDLY_STATUSES[mappedProject.progress_bar_status].text(mappedProject),

    };

    mappedProject.avatarImage = (() => {
      if (mappedProject.conversion_file?.images?.preview) {
        return updateQueryParameters(mappedProject.conversion_file?.images?.preview, {
          height: 350
        });
      }
    })();

    //avatar bgs and icons
    if (mappedProject.hasStoragePermanent) {
      mappedProject.currentStep.avatarBg = 'bg-sage-100/50';
      mappedProject.currentStep.avatarIcon = 'i-ri-check-double-line';
      mappedProject.currentStep.avatarIconClass = 'bg-sage-400';
    } else if (mappedProject.download_expired) {
      mappedProject.currentStep.avatarBg = 'bg-salmon-100/25';
      mappedProject.currentStep.avatarIcon = 'i-ri-close-line';
      mappedProject.currentStep.avatarIconClass = 'bg-salmon-200';
    } else if(mappedProject.gift && !mappedProject.transferred_to && !mappedProject.transferred_from) { //marked as a gift
      mappedProject.currentStep.avatarBg = 'bg-ice-100/50';
      mappedProject.currentStep.avatarIcon = 'i-ri-gift-line';
      mappedProject.currentStep.avatarIconClass = 'bg-ice-600';
    } else {
      switch (mappedProject.progress_bar_status) {
        case 'gifted':
          mappedProject.currentStep.avatarBg = 'bg-gray-100';
          mappedProject.currentStep.avatarIcon = 'i-ri-gift-line';
          mappedProject.currentStep.avatarIconClass = 'bg-gray-400';
          break;
        case 'transferred':
          mappedProject.currentStep.avatarBg = 'bg-gray-100';
          mappedProject.currentStep.avatarIcon = 'i-ri-corner-up-right-line';
          mappedProject.currentStep.avatarIconClass = 'bg-gray-400';
          break;
        default:
          mappedProject.currentStep.avatarBg = 'bg-ice-100/50';
          mappedProject.currentStep.avatarIcon = 'i-ri-loop-right-line';
          mappedProject.currentStep.avatarIconClass = 'bg-ice-600';
      }
    }
  }

  if (mappedProject.progress_bar && mappedProject.progress_bar_status) {
    mappedProject.steps = mappedProject.progress_bar.map(s => {
      s.label = DIGITIZATION_FRIENDLY_STATUSES[s.step]?.label || 'unknown';
      s.isCurrent = s.step === mappedProject.progress_bar_status;
      s.isInProgressWhenCurrent = DIGITIZATION_FRIENDLY_STATUSES[s.step]?.isInProgressWhenCurrent;
      s.isComplete = s.complete;
      s.text = DIGITIZATION_FRIENDLY_STATUSES[s.step]?.text(mappedProject);
      return s;
    });
  }

  //files
  if (mapFiles) {
    mappedProject.files = {};

    if (mappedProject.line_items) {
      //downloads
      mappedProject.downloads = Object.entries(mappedProject.line_items).reduce(
        (downloadItems, [key, lineItem]) => downloadItems.concat(lineItem.map(album => ({
            url: album.download_url,
            type: DIGITIZATION_FILE_TYPES[key].type,
            fileName: album.download_url.slice(album.download_url.lastIndexOf('/') + 1)
          }))
        ), []).sort((a, b) => a.fileName.localeCompare(b.fileName));

      for (const [key, config] of Object.entries(DIGITIZATION_FILE_TYPES)) {
        mappedProject.files[key] = mappedProject.line_items[key]
          .reduce((all, lineItem) => {
            if (key === 'photos') {
              lineItem.files.forEach(file => {
                file.group = {
                  name: `${lineItem.name} (${lineItem.files.length} Items)`,
                  id: lineItem.id
                }
              });
            } else {
              lineItem.files.forEach(file => {
                file.group = {
                  name: `${key[0].toUpperCase()}${key.slice(1, key.length - 1)}`,
                  id: key
                }
              });
            }


            return all.concat(lineItem.files);
          }, [])
          .map(file => mapDigitizationFile({file, config, user: mappedProject.user}));

        if (['audios', 'videos'].includes(key)) {
          mappedProject.files[key].forEach(file => {
            file.group.name = `${file.group.name} (${mappedProject.files[key].length} Items)`;
          });
        }
      }
    }

    mappedProject.line_items = Object.keys(mappedProject.files).reduce((all, key) => all.concat(mappedProject.files[key]), []);
    delete mappedProject.files;
  }

  return mappedProject;
}
